import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { Button } from 'ui-components/components/button';
import { INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';
import { Input, version2 } from 'ui-components/components/input';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import ModalPortal from '../../../../../../components/modal-portal';
import { updateSellerProviderPassword } from '../../../../../../wallet/utilities/api';
import messages from '../messages';
import style from './style.module.less';

const NEW_PASSWORD = 'newPassword';
const CONFIRM_PASSWORD = 'confirmPassword';

const helperTextContent = <FormattedMessage {...messages.passwordsDoNotMatch} />;

const UpdatePasswordModal = ({ sellerLabelProviderId, onClose }) => {
  const intl = useIntl();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswordMatchError, setShowPasswordMatchError] = useState(false);
  const [hasError, setHasError] = useAsyncSafeState(false);

  const onChange = useCallback(
    (e) => {
      if (hasError) {
        setHasError(false);
      }

      if (showPasswordMatchError) {
        setShowPasswordMatchError(false);
      }

      if (e.target.name === NEW_PASSWORD) {
        return setNewPassword(e.target.value);
      }

      setConfirmPassword(e.target.value);
    },
    [setNewPassword, hasError, setHasError, showPasswordMatchError, setShowPasswordMatchError],
  );

  const save = useCallback(() => {
    if (newPassword !== confirmPassword) {
      return setShowPasswordMatchError(true);
    }

    updateSellerProviderPassword(sellerLabelProviderId, newPassword)
      .then(() => {
        onClose();
      })
      .catch(() => setHasError(true));
  }, [sellerLabelProviderId, newPassword, confirmPassword, onClose, setHasError]);

  const inlineMessage = showPasswordMatchError && helperTextContent;
  const inlineMessageType = inlineMessage ? INLINE_MESSAGE_TYPES.error : undefined;

  return (
    <ModalPortal onClose={onClose}>
      <Modal onClose={onClose}>
        <ModalHeader>
          <FormattedMessage {...messages.reauthorize} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          {hasError ? (
            <BannerMessageV2
              className={style['error-banner']}
              content={<FormattedMessage {...messages.updatePasswordError} />}
              status={BannerStatus.Error}
            />
          ) : null}
          <label className={style.description}>
            <FormattedMessage {...messages.updatePasswordDescription} />
          </label>
          <BannerMessageV2
            className={style['info-icon-and-label']}
            content={<FormattedMessage {...messages.updatePasswordLockoutWarning} />}
            status={BannerStatus.Info}
          />
          <div className={style['password-inputs']}>
            <Input
              className={style.password}
              label={intl.formatMessage(messages.newPasswordLabel)}
              name={NEW_PASSWORD}
              onChange={onChange}
              type='password'
              value={newPassword}
              version={version2}
            />
            <Input
              inlineMessage={inlineMessage}
              inlineMessageType={inlineMessageType}
              label={intl.formatMessage(messages.confirmPasswordLabel)}
              name={CONFIRM_PASSWORD}
              onChange={onChange}
              type='password'
              value={confirmPassword}
              version={version2}
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onClose} theme='tertiary'>
            <FormattedMessage {...messages.cancel} />
          </Button>
          <Button disabled={!newPassword || !confirmPassword} onClick={save} theme='primary'>
            <FormattedMessage {...messages.save} />
          </Button>
        </ModalFooter>
      </Modal>
    </ModalPortal>
  );
};

UpdatePasswordModal.propTypes = {
  sellerLabelProviderId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default UpdatePasswordModal;
